import { PowerBIEmbed } from 'powerbi-client-react';
import { FC, useEffect, useMemo, useRef } from 'react';
import { models } from 'powerbi-client';
import { useGetPowerBiTokenQuery } from 'store/_Api/PowerBi';
import { useParams } from 'react-router';
import useBaseLinks from 'store/Auth/Links/useBaseLinks';
import { skipToken } from '@reduxjs/toolkit/query';
import SuspenseLoader from 'components/SuspenseLoader';
import './PowerBiGraph.scss';
import { useTranslation } from 'react-i18next';

type Props = {
  pageName?: string;
  reportId?: string;
};

const PowerBiGraph: FC<Props> = ({
  pageName,
  reportId = '4f49b5f9-e65c-46d2-b0e5-2cb2b3a0f4eb',
}) => {
  const { getLink } = useBaseLinks();
  const powerBiLink = getLink('generate-powerbi-token') ?? '';
  const { data, isLoading } = useGetPowerBiTokenQuery(powerBiLink ?? skipToken);
  const containerRef = useRef<HTMLDivElement>(null);
  const accessToken = data?.powerbi_token ?? '';
  const { penNumber } = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        containerRef.current.style.height = `${(width * 9) / 16}px`;
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [containerRef.current]);

  const filters = useMemo(() => {
    return [
      {
        $schema: 'http://powerbi.com/product/schema#basic',
        target: {
          table: 'remora-test mlGrowth (2)',
          column: 'PenNumber',
        },
        operator: 'In',
        values: [Number(penNumber)],
        filterType: models.FilterType.Basic,
      },
    ];
  }, [penNumber]);

  const settings = {
    panes: {
      filters: {
        expanded: false,
        visible: false,
      },
      pageNavigation: {
        visible: false,
      },
    },
    layoutType: models.LayoutType.Custom,
    customLayout: {
      displayOption: models.DisplayOption.FitToPage,
    },
    background: models.BackgroundType.Transparent,

    visualSettings: {
      visualHeaders: [
        {
          settings: {
            visible: false,
          },
        },
      ],
    },
  };

  if (!penNumber) {
    return (
      <div className="no-content">
        {t('No id found, Unable to render graph')}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="power-bi-wrapper">
      {isLoading ? (
        <div className="no-content">
          <SuspenseLoader isFullScreen={false} />
        </div>
      ) : (
        <PowerBIEmbed
          embedConfig={{
            type: 'report',
            id: reportId,
            accessToken,
            tokenType: models.TokenType.Embed,
            pageName,
            filters,
            settings,
          }}
          eventHandlers={
            new Map([
              [
                'error',
                (event) => {
                  console.error(event?.detail);
                },
              ],
            ])
          }
          cssClassName="powerbi-embed-full"
        />
      )}
    </div>
  );
};

export default PowerBiGraph;
